/* ──────────────────────────────────────────────────────────────
 *  useParameterRanges — fetch dynamic min/max ranges from backend
 *
 *  Returns parameter ranges (packaging + stiffness targets) derived
 *  from training data for the current bushing type.
 * ────────────────────────────────────────────────────────────── */

import { useEffect, useRef, useState } from 'react';
import { useBushingStore } from '@/stores/useBushingStore';
import { fetchParameterRanges, type ParameterRangesResponse, type ParamRange } from '@/api/trainingDataApi';
import type { BushingType } from '@/types/bushing';
import type { PackagingInputs } from '@/stores/useInputStore';

/** Ranges keyed by frontend field name. */
export interface DynamicRanges {
  packaging: Partial<Record<keyof PackagingInputs, ParamRange>>;
  targets: Record<string, ParamRange>;
}

/**
 * Map backend response keys to frontend packaging field names.
 * The mapping differs per bushing type because the training data
 * columns differ.
 */
export function mapRangesToFrontend(
  raw: ParameterRangesResponse,
  bushingType: BushingType,
): DynamicRanges {
  const packaging: Partial<Record<keyof PackagingInputs, ParamRange>> = {};
  const targets: Record<string, ParamRange> = {};

  // Stiffness params (always direct key match)
  const stiffnessKeys = ['Cx', 'Cy', 'Cz', 'Torsion', 'Conical', 'Conical Void', 'Conical Solid'];
  for (const key of stiffnessKeys) {
    if (raw[key]) {
      targets[key] = raw[key];
    }
  }

  switch (bushingType) {
    case 'A1':
    case 'C1': {
      if (raw.core_length) packaging.coreLength = raw.core_length;
      if (raw.outer_sleeve_length) packaging.outerSleeveLength = raw.outer_sleeve_length;
      if (raw.control_arm_id) packaging.controlArmId = raw.control_arm_id;
      if (raw.core_outer_diameter) {
        packaging.coreOdMin = raw.core_outer_diameter;
        packaging.coreOdMax = raw.core_outer_diameter;
      }
      break;
    }
    case 'B1': {
      if (raw.core_length) packaging.coreLength = raw.core_length;
      if (raw.outer_sleeve_length) packaging.outerSleeveLength = raw.outer_sleeve_length;
      // B1 uses outer_sleeve_diameter_mounted for control arm ID
      if (raw.outer_sleeve_diameter_mounted) packaging.controlArmId = raw.outer_sleeve_diameter_mounted;
      if (raw.core_outer_diameter) {
        packaging.coreOdMin = raw.core_outer_diameter;
        packaging.coreOdMax = raw.core_outer_diameter;
      }
      break;
    }
    case 'D1': {
      // D1 uses different column names
      if (raw.length_arm_inner_core) packaging.coreLength = raw.length_arm_inner_core;
      if (raw.length_arm_outer_core) packaging.outerSleeveLength = raw.length_arm_outer_core;
      if (raw.control_arm_id) packaging.controlArmId = raw.control_arm_id;
      if (raw.diam_center_inner_core) {
        packaging.coreOdMin = raw.diam_center_inner_core;
        packaging.coreOdMax = raw.diam_center_inner_core;
      }
      break;
    }
    case 'BYD EV Voided':
    case 'BYD EV Solid':
      // BYD types only have stiffness targets, no packaging ranges
      break;
  }

  return { packaging, targets };
}

/**
 * Hook — fetches parameter ranges for the current bushing type from the
 * backend API and maps them to frontend field names.
 */
export function useParameterRanges(): DynamicRanges {
  const bushingType = useBushingStore((s) => s.bushingType);
  const [ranges, setRanges] = useState<DynamicRanges>({ packaging: {}, targets: {} });
  const lastFetchedType = useRef<string | null>(null);

  useEffect(() => {
    if (lastFetchedType.current === bushingType) return;
    lastFetchedType.current = bushingType;

    fetchParameterRanges(bushingType)
      .then((raw) => {
        setRanges(mapRangesToFrontend(raw, bushingType));
      })
      .catch((err) => {
        console.error('[useParameterRanges] Failed to load ranges:', err);
        // Fall back to empty ranges on error
        setRanges({ packaging: {}, targets: {} });
      });
  }, [bushingType]);

  return ranges;
}
